/**
 * useTutorialTrigger - Hook for triggering tutorials based on routes and user actions
 * Handles route-based tutorial triggers and special triggers like roadmap settings
 */

import { useEffect, useCallback, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTutorial } from '../../contexts/TutorialContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useProject } from '../../contexts/ProjectContext';
import tutorialService from '../../services/tutorialService';
import logger from '../../utils/logger';

const useTutorialTrigger = () => {
  const { shouldShowTutorial, startTutorial, isLoading } = useTutorial();
  const location = useLocation();
  const { user } = useAuth();
  const { loading: organizationLoading, currentOrganization } = useOrganization();
  const { loading: projectLoading } = useProject();
  const [triggeredTutorials, setTriggeredTutorials] = useState(new Set());

  // Track current pathname in a ref so setTimeout callbacks can check it
  const pathnameRef = useRef(location.pathname);
  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  // Check if user is new (no completed tutorials)
  const isNewUser = user && !user.has_completed_tutorials;

  // Find matching tutorial for current route using database route_pattern
  const findMatchingTutorial = useCallback((pathname) => {
    return tutorialService.findTutorialForRoute(pathname);
  }, []);

  // Trigger tutorial with delay and checks
  const triggerTutorial = useCallback((tutorialKey, delay = 1000) => {
    if (!tutorialKey || triggeredTutorials.has(tutorialKey)) {
      return;
    }

    const userContext = { isNewUser, user };

    if (shouldShowTutorial(tutorialKey, userContext)) {
      logger.debug(`useTutorialTrigger: Triggering tutorial ${tutorialKey} with ${delay}ms delay`);

      // Capture pathname now; abort if user navigates away before delay fires
      const triggerPathname = location.pathname;

      setTimeout(() => {
        if (pathnameRef.current !== triggerPathname) {
          logger.debug(`useTutorialTrigger: Aborting tutorial ${tutorialKey} — user navigated away before delay`);
          return;
        }
        startTutorial(tutorialKey);
        setTriggeredTutorials(prev => new Set([...prev, tutorialKey]));
      }, delay);

      return true;
    } else {
      logger.debug(`useTutorialTrigger: Tutorial ${tutorialKey} should not be shown`);
      return false;
    }
  }, [shouldShowTutorial, startTutorial, triggeredTutorials, isNewUser, user, location.pathname]);

  // When the route changes, drop any triggered-but-not-completed tutorials that
  // belong to a different route from the set. This allows them to re-trigger the
  // next time the user visits that page (e.g. after navigating away mid-tutorial).
  useEffect(() => {
    setTriggeredTutorials(prev => {
      if (prev.size === 0) return prev;
      const currentRouteKey = tutorialService.findTutorialForRoute(location.pathname);
      const next = new Set();
      for (const key of prev) {
        const progress = tutorialService.userProgress?.get(key);
        const isFinished = progress?.status === 'completed' || progress?.status === 'skipped';
        // Keep: finished tutorials (don't re-show) OR tutorials for the current route (still here)
        if (isFinished || key === currentRouteKey) {
          next.add(key);
        }
        // Drop: pending/in-progress tutorials for other routes — let them re-trigger on return
      }
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Main route-based trigger effect
  useEffect(() => {
    // Don't trigger tutorials if user is not authenticated
    if (!user || isLoading || organizationLoading || projectLoading) {
      return;
    }

    const tutorialKey = findMatchingTutorial(location.pathname);
    
    if (tutorialKey && !triggeredTutorials.has(tutorialKey)) {
      // Add extra delay for social media tutorial to account for API calls
      const delay = tutorialKey === 'social_media_setup' ? 3000 : 1000;
      triggerTutorial(tutorialKey, delay);
    }
  }, [location.pathname, findMatchingTutorial, triggeredTutorials, triggerTutorial, user, isLoading, organizationLoading, currentOrganization, projectLoading]);

  // Event-based tutorial triggering for modals and other UI actions
  useEffect(() => {
    if (!user || isLoading) return;

    const handleTutorialActionEvent = (event) => {
      const { actionId } = event.detail;
      
      // Map action IDs to tutorial keys
      const actionToTutorialMap = {
        'survey_creation_modal_opened': 'survey_creation'
      };
      
      const tutorialKey = actionToTutorialMap[actionId];
      if (tutorialKey) {
        logger.debug(`useTutorialTrigger: Event-based trigger for ${tutorialKey} (action: ${actionId})`);
        triggerTutorial(tutorialKey, 500);
      }
    };

    window.addEventListener('tutorial-action', handleTutorialActionEvent);
    
    return () => {
      window.removeEventListener('tutorial-action', handleTutorialActionEvent);
    };
  }, [user, isLoading, triggerTutorial]);

  // Special trigger for roadmap settings (when user clicks Roadmap Settings tab)
  const triggerRoadmapSettings = useCallback(() => {
    if (!user) return;
    triggerTutorial('roadmap_settings', 500);
  }, [triggerTutorial, user]);

  // Special trigger for social media setup (when user first visits social media)
  const triggerSocialMediaSetup = useCallback(() => {
    if (!user) return;
    triggerTutorial('social_media_setup', 500);
  }, [triggerTutorial, user]);

  // Special trigger for public roadmap tour (when user first visits public roadmap)
  const triggerPublicRoadmapTour = useCallback(() => {
    if (!user) return;
    triggerTutorial('public_roadmap_tour', 500);
  }, [triggerTutorial, user]);

  // Manual trigger for any tutorial
  const triggerTutorialManually = useCallback((tutorialKey, delay = 0) => {
    triggerTutorial(tutorialKey, delay);
  }, [triggerTutorial]);

  // Reset triggered tutorials (useful for testing or re-triggering)
  const resetTriggeredTutorials = useCallback(() => {
    setTriggeredTutorials(new Set());
  }, []);

  // Check if tutorial has been triggered in this session
  const hasTriggeredTutorial = useCallback((tutorialKey) => {
    return triggeredTutorials.has(tutorialKey);
  }, [triggeredTutorials]);

  // Get tutorial key for current route
  const getCurrentRouteTutorial = useCallback(() => {
    return tutorialService.findTutorialForRoute(location.pathname);
  }, [location.pathname]);

  return {
    // Automatic triggers (handled by the hook)
    currentRouteTutorial: getCurrentRouteTutorial(),
    
    // Manual triggers
    triggerRoadmapSettings,
    triggerSocialMediaSetup,
    triggerPublicRoadmapTour,
    triggerTutorialManually,
    
    // Utilities
    hasTriggeredTutorial,
    resetTriggeredTutorials,
    isNewUser
  };
};

export default useTutorialTrigger;
